import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {PopupItem} from '@mintdata/mint-kit/impl/components/popover/PopupState';
import {
    PopupAnchor,
    PopupKind,
    PopupSize,
    PopupSource,
    SourceAnchor,
    WindowPos
} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const styles = require('@mintdata/mint-kit/impl/components/popover/PopupGlassView.less');
const cx = classnames.bind(styles);

export interface IPopupGlassLayerProps
{
    modalBackgroundClassName?:string;
    popupItem:PopupItem;
}

export class GlassLayerUtil
{
    public static windowPosToCssClass(popupItem:PopupItem):string
    {
        let className;

        const position = popupItem.specProps.position;
        if( position )
        {
            switch( position )
            {
                case WindowPos.CenterTop:
                    className = styles['glassLayerContent_centerTop'];
                    break;
                case WindowPos.CenterMiddle:
                    className = styles['glassLayerContent_centerMiddle'];
                    break;
                case WindowPos.CenterBottom:
                    className = styles['glassLayerContent_centerBottom'];
                    break;
                case WindowPos.LeftTop:
                    className = styles['glassLayerContent_leftTop'];
                    break;
                case WindowPos.LeftMiddle:
                    className = styles['glassLayerContent_leftMiddle'];
                    break;
                case WindowPos.LeftBottom:
                    className = styles['glassLayerContent_leftBottom'];
                    break;
                case WindowPos.RightTop:
                    className = styles['glassLayerContent_rightTop'];
                    break;
                case WindowPos.RightMiddle:
                    className = styles['glassLayerContent_rightMiddle'];
                    break;
                case WindowPos.RightBottom:
                    className = styles['glassLayerContent_rightBottom'];
                    break;
                default:
                    className = '';
            }
        }

        return className;
    }

    public static getWindowBounds(element:HTMLElement):Bounds
    {
        const window = element.ownerDocument.defaultView.window;
        return Bounds.from(0, 0, window.innerWidth, window.innerHeight);
    }

    public static getSourceAnchorPoint(sourceAnchor:SourceAnchor, sourceRect:ClientRect):Point | null
    {
        const {left, top, width, height} = sourceRect;

        switch( sourceAnchor )
        {
            case SourceAnchor.LeftTop:
                return Point.from(left, top);
            case SourceAnchor.LeftMiddle:
                return Point.from(left, top + (height / 2));
            case SourceAnchor.LeftBottom:
                return Point.from(left, top + height);
            case SourceAnchor.RightTop:
                return Point.from(left + width, top);
            case SourceAnchor.RightMiddle:
                return Point.from(left + width, top + (height / 2));
            case SourceAnchor.RightBottom:
                return Point.from(left + width, top + height);
            case SourceAnchor.CenterTop:
                return Point.from(left + (width / 2), top);
            case SourceAnchor.CenterMiddle:
                return Point.from(left + (width / 2), top + (height / 2));
            case SourceAnchor.CenterBottom:
                return Point.from(left + (width / 2), top + height);

            default:
            {
                mk.error(area.Popup, `Unknown source anchor: ${sourceAnchor}`, sourceAnchor);
                return null;
            }
        }
    }

    public static getPopupAnchorPoint(sourceAnchorPoint:Point, popupRect:ClientRect, popupAnchor:PopupAnchor):Point | null
    {
        const {width, height} = popupRect;

        switch( popupAnchor )
        {
            case PopupAnchor.LeftTop:
                return Point.from(sourceAnchorPoint.x, sourceAnchorPoint.y);
            case PopupAnchor.LeftMiddle:
                return Point.from(sourceAnchorPoint.x, sourceAnchorPoint.y - height / 2);
            case PopupAnchor.LeftBottom:
                return Point.from(sourceAnchorPoint.x, sourceAnchorPoint.y - height);
            case PopupAnchor.RightTop:
                return Point.from(sourceAnchorPoint.x - width, sourceAnchorPoint.y);
            case PopupAnchor.RightMiddle:
                return Point.from(sourceAnchorPoint.x - width, sourceAnchorPoint.y - height / 2);
            case PopupAnchor.RightBottom:
                return Point.from(sourceAnchorPoint.x - width, sourceAnchorPoint.y - height);
            case PopupAnchor.CenterTop:
                return Point.from(sourceAnchorPoint.x - width / 2, sourceAnchorPoint.y);
            case PopupAnchor.CenterMiddle:
                return Point.from(sourceAnchorPoint.x - width / 2, sourceAnchorPoint.y - height / 2);
            case PopupAnchor.CenterBottom:
                return Point.from(sourceAnchorPoint.x - width / 2, sourceAnchorPoint.y - height);

            default:
            {
                mk.error(area.Popup, `Unknown popup anchor: ${popupAnchor}`, popupAnchor);
                return null;
            }
        }
    }

    public static sourceAnchorToScreenBounds(source:PopupSource, sourceAnchor:SourceAnchor):Bounds
    {
        if( !source )
        {
            mk.warn(area.Popup, 'PopupSource (source) was undefined in anchorTypeToScreenBounds()');
            return;
        }

        const sourceRect = source instanceof HTMLElement ? source.getBoundingClientRect() : source;

        const sourceAnchorPoint = GlassLayerUtil.getSourceAnchorPoint(sourceAnchor, sourceRect);
        if( !sourceAnchorPoint )
            return null;

        return Bounds.from(sourceAnchorPoint.x, sourceAnchorPoint.y, sourceRect.width, sourceRect.height);
    }

    public static popupAnchorToScreenBounds(sourceAnchorBounds:Bounds, popupRect:ClientRect, popupAnchor:PopupAnchor, popupSize:PopupSize):Bounds
    {
        const popupAnchorPoint = GlassLayerUtil.getPopupAnchorPoint(sourceAnchorBounds.getTopLeft(), popupRect, popupAnchor);
        if( !popupAnchorPoint )
            return null;

        switch( popupSize )
        {
            case PopupSize.WidthOfSource:
                return Bounds.from(popupAnchorPoint.x, popupAnchorPoint.y, sourceAnchorBounds.width, popupRect.height);
            case PopupSize.HeightOfSource:
                return Bounds.from(popupAnchorPoint.x, popupAnchorPoint.y, popupRect.width, sourceAnchorBounds.height);
            case PopupSize.WidthAndHeightOfSource:
                return Bounds.from(popupAnchorPoint.x, popupAnchorPoint.y, sourceAnchorBounds.width, sourceAnchorBounds.height);
            default:
                return Bounds.from(popupAnchorPoint.x, popupAnchorPoint.y, popupRect.width, popupRect.height);
        }
    }

    static flipSourceAnchor(sourceAnchor:SourceAnchor, flipVertically:boolean, flipHorizontally:boolean):SourceAnchor
    {
        if( !flipHorizontally && !flipVertically )
            return sourceAnchor;

        switch( sourceAnchor )
        {
            case SourceAnchor.LeftTop:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.RightBottom;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.RightTop;
                }
                else
                {
                    return SourceAnchor.LeftBottom;
                }
            case SourceAnchor.LeftMiddle:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.RightMiddle;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.RightMiddle;
                }
                else
                {
                    return SourceAnchor.LeftMiddle;
                }
            case SourceAnchor.LeftBottom:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.RightTop;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.RightBottom;
                }
                else
                {
                    return SourceAnchor.LeftTop;
                }
            case SourceAnchor.RightTop:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.LeftBottom;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.LeftTop;
                }
                else
                {
                    return SourceAnchor.RightBottom;
                }
            case SourceAnchor.RightMiddle:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.LeftMiddle;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.LeftMiddle;
                }
                else
                {
                    return SourceAnchor.RightMiddle;
                }
            case SourceAnchor.RightBottom:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.LeftTop;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.LeftBottom;
                }
                else
                {
                    return SourceAnchor.RightTop;
                }
            case SourceAnchor.CenterTop:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.CenterBottom;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.CenterTop;
                }
                else
                {
                    return SourceAnchor.CenterBottom;
                }
            case SourceAnchor.CenterMiddle:
                return SourceAnchor.CenterMiddle;
            case SourceAnchor.CenterBottom:
                if( flipVertically && flipHorizontally )
                {
                    return SourceAnchor.CenterTop;
                }
                else if( flipHorizontally )
                {
                    return SourceAnchor.CenterBottom;
                }
                else
                {
                    return SourceAnchor.CenterTop;
                }

            default:
            {
                mk.error(area.Popup, `Unknown source anchor: ${sourceAnchor}`, sourceAnchor);
                return sourceAnchor;
            }
        }
    }

    static flipPopupAnchor(popoverAnchor:PopupAnchor, flipVertically:boolean, flipHorizontally:boolean):PopupAnchor
    {
        if( !flipHorizontally && !flipVertically )
            return popoverAnchor;

        switch( popoverAnchor )
        {
            case PopupAnchor.LeftTop:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.RightBottom;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.RightTop;
                }
                else
                {
                    return PopupAnchor.LeftBottom;
                }
            case PopupAnchor.LeftMiddle:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.RightMiddle;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.RightMiddle;
                }
                else
                {
                    return PopupAnchor.LeftMiddle;
                }
            case PopupAnchor.LeftBottom:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.RightTop;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.RightBottom;
                }
                else
                {
                    return PopupAnchor.LeftTop;
                }
            case PopupAnchor.RightTop:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.LeftBottom;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.LeftTop;
                }
                else
                {
                    return PopupAnchor.RightBottom;
                }
            case PopupAnchor.RightMiddle:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.LeftMiddle;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.LeftMiddle;
                }
                else
                {
                    return PopupAnchor.RightMiddle;
                }
            case PopupAnchor.RightBottom:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.LeftTop;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.LeftBottom;
                }
                else
                {
                    return PopupAnchor.RightTop;
                }
            case PopupAnchor.CenterTop:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.CenterBottom;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.CenterTop;
                }
                else
                {
                    return PopupAnchor.CenterBottom;
                }
            case PopupAnchor.CenterMiddle:
                return PopupAnchor.CenterMiddle;
            case PopupAnchor.CenterBottom:
                if( flipVertically && flipHorizontally )
                {
                    return PopupAnchor.CenterTop;
                }
                else if( flipHorizontally )
                {
                    return PopupAnchor.CenterBottom;
                }
                else
                {
                    return PopupAnchor.CenterTop;
                }

            default:
            {
                mk.error(area.Popup, `Unknown popover anchor: ${popoverAnchor}`, popoverAnchor);
                return popoverAnchor;
            }
        }
    }
}

export class PopupGlassLayer extends React.Component<IPopupGlassLayerProps, any>
{
    private _popover:HTMLElement | null;

    private _refHandlers = {
        popover: (ref:HTMLElement | null) =>
        {
            this._popover = ref;
        }
    };
    private _onPopoverHtmlElementReady = (popupItem:PopupItem, popupHtmlElement:HTMLElement):void =>
    {
        if( popupHtmlElement ) //&& popupItem.popupSource )
        {
            // reset popup location and size
            popupHtmlElement.setAttribute('style', '');

            let sourceAnchor = SourceAnchor[popupHtmlElement.getAttribute('data-source-anchor')] || popupItem.sourceAnchor;
            let popupAnchor = PopupAnchor[popupHtmlElement.getAttribute('data-popup-anchor')] || popupItem.popupAnchor;

            let popupSize = popupItem.popupSize;

            const popupRect = popupHtmlElement.getBoundingClientRect();
            const windowBounds = GlassLayerUtil.getWindowBounds(popupHtmlElement);

            let sourceAnchorBounds = GlassLayerUtil.sourceAnchorToScreenBounds(popupItem.popupSource, sourceAnchor);
            if( !sourceAnchorBounds )
                return;

            let popupAnchorBounds = GlassLayerUtil.popupAnchorToScreenBounds(sourceAnchorBounds, popupRect, popupAnchor, popupSize);
            if( !popupAnchorBounds )
                return;

            // First step: flip by vertical if need
            let intersectBounds = Bounds.intersect(popupAnchorBounds, windowBounds);
            if( intersectBounds.height != popupAnchorBounds.height )
            {
                const flippedSourceAnchor = GlassLayerUtil.flipSourceAnchor(sourceAnchor, true, false);
                const flippedPopupAnchor = GlassLayerUtil.flipPopupAnchor(popupAnchor, true, false);

                const flippedSourceAnchorBounds = GlassLayerUtil.sourceAnchorToScreenBounds(popupItem.popupSource, flippedSourceAnchor);
                if( flippedSourceAnchorBounds )
                {
                    const flippedPopupAnchorBounds = GlassLayerUtil.popupAnchorToScreenBounds(flippedSourceAnchorBounds, popupRect, flippedPopupAnchor, popupSize);
                    if( flippedPopupAnchorBounds )
                    {
                        const originalIntersectBounds = Bounds.intersect(popupAnchorBounds, windowBounds);
                        const flippedIntersectBounds = Bounds.intersect(flippedPopupAnchorBounds, windowBounds);

                        // select best case
                        if( flippedIntersectBounds.height > originalIntersectBounds.height )
                        {
                            popupAnchorBounds = flippedPopupAnchorBounds;
                            sourceAnchor = flippedSourceAnchor;
                            popupAnchor = flippedPopupAnchor;
                        }
                    }
                }
            }

            // Second step: flip by horizontal if need
            intersectBounds = Bounds.intersect(popupAnchorBounds, windowBounds);
            if( intersectBounds.width != popupAnchorBounds.width )
            {
                const flippedSourceAnchor = GlassLayerUtil.flipSourceAnchor(sourceAnchor, false, true);
                const flippedPopupAnchor = GlassLayerUtil.flipPopupAnchor(popupAnchor, false, true);

                const flippedSourceAnchorBounds = GlassLayerUtil.sourceAnchorToScreenBounds(popupItem.popupSource, flippedSourceAnchor);
                if( flippedSourceAnchorBounds )
                {
                    const flippedPopupAnchorBounds = GlassLayerUtil.popupAnchorToScreenBounds(flippedSourceAnchorBounds, popupRect, flippedPopupAnchor, popupSize);
                    if( flippedPopupAnchorBounds )
                    {
                        const originalIntersectBounds = Bounds.intersect(popupAnchorBounds, windowBounds);
                        const flippedIntersectBounds = Bounds.intersect(flippedPopupAnchorBounds, windowBounds);

                        // select best case
                        if( flippedIntersectBounds.width > originalIntersectBounds.width )
                        {
                            popupAnchorBounds = flippedPopupAnchorBounds;
                            sourceAnchor = flippedSourceAnchor;
                            popupAnchor = flippedPopupAnchor;
                        }
                    }
                }
            }

            // Third step: shift if need
            if( !this.props.popupItem.specProps.stickToSource )
            {
                intersectBounds = Bounds.intersect(popupAnchorBounds, windowBounds);
                if( intersectBounds.width < popupAnchorBounds.width )
                {
                    const x = popupAnchorBounds.x - (popupAnchorBounds.width - intersectBounds.width) - 5;
                    popupAnchorBounds.x = Math.max(0, x);
                }

                if( intersectBounds.height < popupAnchorBounds.height )
                {
                    const y = popupAnchorBounds.y - (popupAnchorBounds.height - intersectBounds.height) - 5;
                    popupAnchorBounds.y = Math.max(0, y);
                }
            }

            let computedStyle = this._computeCssStyle(windowBounds, popupAnchorBounds, sourceAnchorBounds, popupSize);
            const styleFromProps:React.CSSProperties = this.props.popupItem.specProps.style;

            if( styleFromProps )
                computedStyle += `${Object.entries(styleFromProps).reduce((styleStr, [propName, propValue]) => (
                    `${styleStr}${propName.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${propValue};`
                ), '')}`;

            // set new popup location and size
            popupHtmlElement.setAttribute('style', `${computedStyle}`);
            popupHtmlElement.setAttribute('data-source-anchor', SourceAnchor[sourceAnchor]);
            popupHtmlElement.setAttribute('data-popup-anchor', PopupAnchor[popupAnchor]);
        }
    };
    private _forceRefresh = ():void =>
    {
        this.forceUpdate();

        const {popupItem} = this.props;
        requestAnimationFrame(this._onPopoverHtmlElementReady.bind(this, popupItem, this._popover));
    };

    public componentDidMount():void
    {
        const {popupItem} = this.props;

        if( popupItem )
            popupItem.forceHandler = this._forceRefresh;

        requestAnimationFrame(this._onPopoverHtmlElementReady.bind(this, popupItem, this._popover));
    }

    public componentWillReceiveProps(newProps:IPopupGlassLayerProps):void
    {
        const {popupItem} = newProps;

        if( popupItem )
            popupItem.forceHandler = this._forceRefresh;
    }

    public render():JSX.Element
    {
        const {popupItem} = this.props;

        if( !popupItem )
            return null;

        return this._renderLayerBackground(popupItem);
    }

    private _renderWindow(popupItem:PopupItem):JSX.Element
    {
        const {
            popupItem: {specProps},
            modalBackgroundClassName
        } = this.props;

        const {className, style, disablePointerEvents} = specProps;

        const layerContent = styles.glassLayerContent;
        const windowGlassLayerContent = styles.windowGlassLayerContent;
        const windowPosClassName = GlassLayerUtil.windowPosToCssClass(popupItem);

        return (
            <div
                className={cx(
                    'glassLayer_windowBackground',
                    modalBackgroundClassName,
                    {disablePointerEvents},
                    className
                )}
                style={style}
            >
                <div
                    data-popupid={popupItem.id}
                    className={`${windowPosClassName} ${layerContent} ${windowGlassLayerContent}`}
                >
                    {popupItem.viewElement}
                </div>
            </div>
        );
    }

    private _renderPopover(popupItem:PopupItem):JSX.Element
    {
        const {popupItem: {specProps}} = this.props;
        const {className, style, disablePointerEvents} = specProps;

        if( !popupItem.popupSource )
        {
            console.warn('popupSource not defined');
            return null;
        }

        const screenBounds = GlassLayerUtil.sourceAnchorToScreenBounds(popupItem.popupSource, popupItem.sourceAnchor);

        const defaultStyle = {position: 'absolute', left: `${screenBounds.x}px`, top: `${screenBounds.y}px`} as any;

        return (
            <div
                ref={this._refHandlers.popover}
                data-popupid={popupItem.id}
                className={cx('glassLayerContent', {disablePointerEvents}, className)}
                style={{...defaultStyle, ...style}}
            >
                {popupItem.viewElement}
            </div>
        );
    }

    private _computeCssStyle(windowBounds:Bounds, popupAnchorBounds:Bounds, sourceAnchorBounds:Bounds, popupSize:PopupSize):string
    {
        const intersectBounds = Bounds.intersect(popupAnchorBounds, windowBounds);

        let needScroll = false;

        let left = popupAnchorBounds.x;
        let top = popupAnchorBounds.y;

        let cssStyle = `position:absolute;`;

        if( intersectBounds.width != popupAnchorBounds.width )
        {
            left = Math.max(intersectBounds.x, popupAnchorBounds.x);

            const width = Math.min(intersectBounds.width, popupAnchorBounds.width);
            cssStyle += ` width:${width}px;`;
            needScroll = true;
        }
        else
        {
            switch( popupSize )
            {
                case PopupSize.WidthOfSource:
                case PopupSize.WidthAndHeightOfSource:
                    cssStyle += `  width:${sourceAnchorBounds.width}px;`;
                    break;
            }
        }

        cssStyle += ` left:${left}px;`;

        if( intersectBounds.height != popupAnchorBounds.height )
        {
            top = Math.max(intersectBounds.y, popupAnchorBounds.y);

            const height = Math.min(intersectBounds.height, popupAnchorBounds.height);
            cssStyle += `  height:${height}px;`;
            needScroll = true;
        }
        else
        {
            switch( popupSize )
            {
                case PopupSize.HeightOfSource:
                case PopupSize.WidthAndHeightOfSource:
                    cssStyle += `  height:${sourceAnchorBounds.height}px;`;
                    break;
            }
        }

        cssStyle += ` top:${top}px;`;

        if( needScroll )
        {
            cssStyle += ` overflow-y: auto;`;
        }

        mk.debug(area.Popup, `css style: ${cssStyle}`);

        return cssStyle;
    }

    private _renderLayerContent(popupItem:PopupItem):JSX.Element
    {
        const kind = popupItem.kind;

        if( kind === PopupKind.Window )
            return this._renderWindow(popupItem);
        else if( kind === PopupKind.Popover )
            return this._renderPopover(popupItem);
        else
        {
            mk.error(area.Popup, `Unknown popup kind: ${kind}`, popupItem);
            return null;
        }
    }

    private _renderLayerBackground(popupItem:PopupItem):JSX.Element
    {
        return this._renderLayerContent(popupItem);
    }
}
